/**
 * 1.9.1: what an asset's status pill says. A ready asset shows where its original is (Online, Archived, Restoring…,
 * Offline) instead of "Ready"; every other status shows as before. The stored status and the filters don't change.
 */
import type { AssetAvailability, AssetStatus } from '@prism/contracts';

export const STATUS_LABEL: Record<AssetStatus, string> = {
  placeholder: 'Placeholder',
  registered: 'Queued',
  recording: 'Recording',
  processing: 'Processing',
  ready: 'Ready',
  partial: 'Partially ready',
  failed: 'Failed',
};

/** The pill's look: a status colour class, or for a ready asset its availability (`online`, `archived`, …). */
export type PillTone = AssetStatus | 'online' | 'archived' | 'restoring' | 'offline';

export interface StatusPill {
  label: string;
  tone: PillTone;
  /** The tooltip, for the states that need saying what they mean. */
  title?: string;
}

export const AVAILABILITY_TITLES = {
  online: 'The original is on a normal storage tier',
  archived: 'The original is only in an archive tier; the proxy plays. Restore it to export or download.',
  restoring: 'The original is being restored from the archive tier',
  offline: 'The original’s storage is offline.',
} as const;

/** The pill for a status and availability; `unknown` (not checked yet) shows Online. */
export function statusPill(status: AssetStatus, availability: AssetAvailability | null | undefined): StatusPill {
  if (status !== 'ready') return { label: STATUS_LABEL[status] ?? status, tone: status };
  switch (availability) {
    case 'archived': return { label: 'Archived', tone: 'archived', title: AVAILABILITY_TITLES.archived };
    case 'restoring': return { label: 'Restoring…', tone: 'restoring', title: AVAILABILITY_TITLES.restoring };
    case 'offline': return { label: 'Offline', tone: 'offline', title: AVAILABILITY_TITLES.offline };
    default: return { label: 'Online', tone: 'online', title: AVAILABILITY_TITLES.online };
  }
}
